import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  FiMenu,
  FiSearch,
  FiBell,
  FiChevronsLeft,
  FiChevronsRight,
  FiCheckCircle,
  FiInfo,
  FiAlertTriangle,
  FiDollarSign,
  FiLifeBuoy,
  FiCheck,
  FiX,
  FiArrowRight,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { LogOut, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/axios";

const searchableRoutes = [
  { label: "Dashboard", to: "/", category: "Overview" },
  { label: "Financial Analytics", to: "/analytics", category: "Analytics" },
  { label: "Game Analytics & Reports", to: "/game-analytics", category: "Analytics" },
  { label: "Members / Users", to: "/users", category: "Players" },
  { label: "KYC Verification", to: "/kyc-verification", category: "Compliance" },
  { label: "AMOE Mail-In Verification", to: "/amoe-management", category: "Sweepstakes" },
  { label: "Self-Exclusion Management", to: "/self-exclusion", category: "Compliance" },
  { label: "Bonus Management", to: "/bonus-management", category: "Promotions" },
  { label: "Bonus & Penalty", to: "/bonus-and-panelty", category: "Wallets" },
  { label: "Penalty & Bonus History", to: "/bonus-and-panelty-history", category: "Wallets" },
  { label: "Coin Purchases (Recharges)", to: "/recharge-list", category: "Finance" },
  { label: "Prize Redemptions (Withdrawals)", to: "/withdrawal-list", category: "Finance" },
  { label: "Support Tickets - Pending", to: "/pending-issues", category: "Support" },
  { label: "Support Tickets - Resolved", to: "/resolved-issues", category: "Support" },
  { label: "Deposit Bonus Rules", to: "/settings/deposit/bonus", category: "Settings" },
  { label: "State Restrictions", to: "/settings/states", category: "Settings" },
  { label: "Country Restrictions", to: "/settings/countries", category: "Settings" },
  { label: "General Settings", to: "/settings/general", category: "Settings" },
  { label: "API Bet History", to: "/api-management", category: "System" },
  { label: "Admin Profile & Accounts", to: "/admin", category: "Admin" },
];

const Navbar = ({ onToggleSidebar, onCollapseSidebar, isCollapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [openMenu, setOpenMenu] = useState(false);
  const menuRef = useRef(null);

  // Notification Bell State
  const [openNotifications, setOpenNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationRef = useRef(null);

  // Command Palette State
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const paletteRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      setLoadingNotifications(true);
      const res = await api.get("/notifications");
      if (res.data?.status) {
        setNotifications(res.data.data || []);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch (err) {
      console.warn("Could not fetch notifications:", err);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Handle hotkeys (⌘K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      } else if (e.key === "Escape") {
        setPaletteOpen(false);
        setOpenNotifications(false);
        setOpenMenu(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setOpenNotifications(false);
      }
      if (paletteRef.current && !paletteRef.current.contains(event.target)) {
        setPaletteOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Mark notification(s) as read
  const handleMarkAsRead = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      const payload = id === "all" ? { markAll: true } : { notificationId: id };
      const res = await api.post("/notifications/mark-read", payload);
      if (res.data?.status) {
        if (id === "all") {
          setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
          setUnreadCount(0);
        } else {
          setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
          );
          setUnreadCount((c) => Math.max(0, c - 1));
        }
      }
    } catch (err) {
      console.warn("Error marking notification read:", err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "deposit":
      case "financial":
        return <FiDollarSign className="text-emerald-500" size={16} />;
      case "kyc":
      case "compliance":
      case "self_exclusion":
        return <FiAlertTriangle className="text-amber-500" size={16} />;
      case "support":
        return <FiLifeBuoy className="text-blue-500" size={16} />;
      case "system":
        return <FiCheckCircle className="text-purple-500" size={16} />;
      default:
        return <FiInfo className="text-slate-500" size={16} />;
    }
  };

  const formatTimeAgo = (dateStr) => {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const filteredRoutes = searchableRoutes.filter(
    (r) =>
      r.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInitials = (name) => {
    if (!name) return "AD";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <>
      <header className="h-16 bg-white/95 backdrop-blur-md border-b border-slate-200/80 px-4 md:px-6 flex items-center justify-between sticky top-0 z-20">
        {/* Left: Sidebar toggle + Quick Command trigger */}
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleSidebar}
            className="md:hidden p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          >
            <FiMenu className="text-xl" />
          </button>

          <button
            onClick={onCollapseSidebar}
            className="hidden md:flex p-2 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            {isCollapsed ? <FiChevronsRight className="text-lg" /> : <FiChevronsLeft className="text-lg" />}
          </button>

          {/* Search Bar / Command Palette Trigger */}
          <div
            onClick={() => setPaletteOpen(true)}
            className="hidden sm:flex items-center bg-slate-100/90 hover:bg-slate-100 cursor-pointer rounded-xl px-3.5 py-2 gap-2.5 text-slate-400 hover:text-slate-600 border border-slate-200/60 transition-all w-60 md:w-72"
          >
            <FiSearch size={15} />
            <span className="text-xs text-slate-500 font-medium flex-1">Quick search commands...</span>
            <kbd className="hidden md:inline-flex items-center gap-0.5 text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-500 font-semibold shadow-xs">
              ⌘K
            </kbd>
          </div>
        </div>

        {/* Right: Quick actions + Notification + User Chip */}
        <div className="flex items-center gap-3 md:gap-4">
          {/* Notifications Bell */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => setOpenNotifications((prev) => !prev)}
              className="relative p-2.5 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors focus:outline-none"
              title="Notifications"
            >
              <FiBell size={18} />
              {unreadCount > 0 && (
                <span className="absolute 1.5 top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold flex items-center justify-center rounded-full bg-rose-500 text-white shadow-sm ring-2 ring-white animate-pulse">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown Panel */}
            {openNotifications && (
              <div className="absolute right-0 mt-3 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200/90 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-4 py-3.5 bg-slate-50/80 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                      Platform Alerts
                    </h3>
                    {unreadCount > 0 && (
                      <span className="bg-rose-100 text-rose-700 text-[10px] px-2 py-0.5 rounded-full font-bold">
                        {unreadCount} unread
                      </span>
                    )}
                  </div>

                  {unreadCount > 0 && (
                    <button
                      onClick={(e) => handleMarkAsRead("all", e)}
                      className="text-xs text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-1 cursor-pointer"
                    >
                      <FiCheckCircle size={12} />
                      Mark all read
                    </button>
                  )}
                </div>

                <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                  {loadingNotifications && notifications.length === 0 ? (
                    <div className="py-8 text-center text-xs text-slate-400">Loading notifications...</div>
                  ) : notifications.length === 0 ? (
                    <div className="py-8 text-center text-xs text-slate-400 flex flex-col items-center gap-1.5">
                      <FiCheckCircle className="text-emerald-500" size={24} />
                      <span className="font-medium text-slate-600">All caught up!</span>
                      <span className="text-[11px] text-slate-400">No unread notifications</span>
                    </div>
                  ) : (
                    notifications.map((item) => (
                      <div
                        key={item.id}
                        className={`p-3.5 hover:bg-slate-50/80 transition flex items-start gap-3 cursor-pointer ${
                          !item.is_read ? "bg-amber-50/30" : ""
                        }`}
                        onClick={() => {
                          if (!item.is_read) handleMarkAsRead(item.id);
                          if (item.link) navigate(item.link);
                          setOpenNotifications(false);
                        }}
                      >
                        <div className="p-2 rounded-xl bg-slate-100 flex-shrink-0 mt-0.5">
                          {getNotificationIcon(item.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-xs font-bold text-slate-800 truncate">
                              {item.title}
                            </span>
                            <span className="text-[10px] text-slate-400 whitespace-nowrap">
                              {formatTimeAgo(item.created_at)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                            {item.message}
                          </p>
                        </div>
                        {!item.is_read && (
                          <button
                            onClick={(e) => handleMarkAsRead(item.id, e)}
                            className="text-slate-400 hover:text-amber-600 p-1 rounded-lg hover:bg-slate-200/50"
                            title="Mark as read"
                          >
                            <FiCheck size={14} />
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User Profile Chip */}
          <div className="relative" ref={menuRef}>
            <div
              className="flex items-center gap-3 p-1.5 pl-3 rounded-xl hover:bg-slate-100 cursor-pointer border border-transparent hover:border-slate-200 transition-all"
              onClick={() => setOpenMenu((prev) => !prev)}
            >
              <div className="hidden sm:block text-right">
                <p className="text-xs font-bold text-slate-800 leading-tight">
                  {user?.name || user?.username || "Admin"}
                </p>
                <div className="flex items-center justify-end gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
                    {user?.role === "client_admin" ? "Client Admin" : "Super Admin"}
                  </span>
                </div>
              </div>

              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-amber-500 to-yellow-400 text-slate-950 flex items-center justify-center font-bold text-xs shadow-sm">
                {getInitials(user?.name || user?.username || "Admin")}
              </div>
            </div>

            {/* User Dropdown */}
            {openMenu && (
              <div className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-xl border border-slate-200/90 z-50 overflow-hidden py-1.5">
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="text-xs font-bold text-slate-800">{user?.name || "Admin"}</p>
                  <p className="text-[11px] text-slate-400 truncate mt-0.5">
                    {user?.email || "admin@treasurevaultarcade.com"}
                  </p>
                </div>

                <div className="p-1.5">
                  <button
                    onClick={() => {
                      navigate("/admin");
                      setOpenMenu(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <Shield size={14} className="text-amber-500" />
                    Security & Passwords
                  </button>

                  <button
                    onClick={() => {
                      logout();
                      setOpenMenu(false);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                  >
                    <LogOut size={14} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── COMMAND PALETTE MODAL (⌘K) ── */}
      {paletteOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-start justify-center pt-20 p-4">
          <div
            ref={paletteRef}
            className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
          >
            <div className="p-3.5 border-b border-slate-100 flex items-center gap-3 bg-slate-50/50">
              <FiSearch className="text-slate-400" size={18} />
              <input
                type="text"
                autoFocus
                placeholder="Type a page or command (e.g. KYC, Members, AMOE)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent text-sm text-slate-800 outline-none w-full placeholder:text-slate-400"
              />
              <button
                onClick={() => setPaletteOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200/50"
              >
                <FiX size={16} />
              </button>
            </div>

            <div className="max-h-72 overflow-y-auto p-2">
              {filteredRoutes.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-400">
                  No pages match "{searchQuery}"
                </div>
              ) : (
                filteredRoutes.map((route) => (
                  <button
                    key={route.to}
                    onClick={() => {
                      navigate(route.to);
                      setPaletteOpen(false);
                      setSearchQuery("");
                    }}
                    className="w-full flex items-center justify-between p-2.5 rounded-xl hover:bg-amber-50 hover:text-amber-900 text-slate-700 transition-colors group text-left"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 group-hover:bg-amber-100 group-hover:text-amber-800">
                        {route.category}
                      </span>
                      <span className="text-xs font-semibold">{route.label}</span>
                    </div>
                    <FiArrowRight
                      size={14}
                      className="text-slate-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"
                    />
                  </button>
                ))
              )}
            </div>

            <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-400 flex items-center justify-between">
              <span>Use <kbd className="px-1 bg-white border border-slate-200 rounded">↑</kbd> <kbd className="px-1 bg-white border border-slate-200 rounded">↓</kbd> to navigate</span>
              <span>Press <kbd className="px-1 bg-white border border-slate-200 rounded">ESC</kbd> to close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
